import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO — single declarative wrapper for per-route metadata.
 * Sets <title>, meta description, canonical, Open Graph / Twitter cards,
 * and optional JSON-LD structured data.
 *
 * iter100 — Branding standardization (P6) + schema helpers (P4):
 *   - Default tagline: "NoFee — 0% Commission Restaurant Ordering Platform"
 *   - Title suffix changed to "| NoFee" (pipe — industry standard for SaaS SERPs)
 *   - Re-export helper schema builders for SoftwareApplication / Organization / FAQPage
 *
 * Props:
 *   - title (string)              Window/SERP title (appended with " | NoFee" unless brand=false)
 *   - description (string)        ≤160-char meta description
 *   - path (string)               Path on nofee.restaurant for canonical + og:url
 *   - image (string)              Absolute URL of og:image / twitter:image
 *   - type (string)               og:type. "website" default; use "restaurant.menu" for menus
 *   - brand (bool)                Append " | NoFee" suffix to title. Defaults true
 *   - noindex (bool)              Add robots noindex,nofollow when true
 *   - jsonLd (object|array)       Structured data; pass single object or @graph array
 */
const BASE_URL = 'https://nofee.restaurant';
const DEFAULT_IMAGE = `${BASE_URL}/brand/og-default.jpg`;
const DEFAULT_TITLE = 'NoFee — 0% Commission Restaurant Ordering Platform';
const DEFAULT_DESCRIPTION =
  'NoFee is the 0% commission restaurant ordering platform. Direct Stripe payouts — better prices for customers, more profit for restaurants.';

const SEO = ({
  title,
  description,
  path = '',
  image,
  type = 'website',
  brand = true,
  noindex = false,
  jsonLd = null,
}) => {
  const fullTitle = brand && title && !title.includes('NoFee')
    ? `${title} | NoFee`
    : (title || DEFAULT_TITLE);
  const url = `${BASE_URL}${path}`;
  const ogImage = image || DEFAULT_IMAGE;
  const desc = description || DEFAULT_DESCRIPTION;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="NoFee" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Structured Data (Schema.org) — emits Rich Results on Google SERPs */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

/* ============================================================================
 * Schema.org helpers — iter100
 * Centralised here so every page emits canonical Rich-Result-ready JSON-LD.
 * ============================================================================ */

export const ORG_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${BASE_URL}/#org`,
  name: 'NoFee',
  alternateName: 'NoFee.restaurant',
  url: BASE_URL,
  logo: `${BASE_URL}/brand/og-default.jpg`,
  description: '0% commission restaurant ordering platform. Direct Stripe payouts to restaurants.',
  sameAs: [],
};

export const SOFTWARE_APP_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  '@id': `${BASE_URL}/#software`,
  name: 'NoFee',
  applicationCategory: 'BusinessApplication',
  description: 'Commission-free online ordering, digital menus, and direct payouts for restaurants.',
  url: BASE_URL,
  operatingSystem: 'Web',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    description: '0% commission on every order. Restaurant subscription only.',
  },
  publisher: { '@id': `${BASE_URL}/#org` },
};

/**
 * Build a Schema.org FAQPage payload from an array of `{ question, answer }`.
 * Pass the result via the `jsonLd` prop on any FAQ page.
 */
export const buildFaqSchema = (items = []) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
});

export default SEO;
